import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { snakeCase } from 'lodash';

import Button from '../../library/button/button';
import Gauge from '../../library/gauge/gauge';
import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';
import TreadLifeEstimate from '../tread-life-estimate/tread-life-estimate';

import {
  useAemContent,
  useFitment,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { calculateLifeExpectancy } from '../../shared/utils/treadwell/treadwell';

import {
  CmsPromotionBannerComponent,
  CmsToolTips,
  CmsToolTipsComponent,
  FuelTypeEnum,
  Maybe,
  ProductData,
  TreadWellPriorityEnum
} from '../../shared/types/graphql-types';
import { AEM_CONTENT_PAGE_IDS } from '../../shared/constants/cms';

import './product-first-priority-details.scss';

interface AemContentTypes {
  evRange: Maybe<CmsToolTips>;
  evRangePending: Maybe<CmsToolTips>;
  fuelEfficiency: Maybe<CmsToolTips>;
  fuelEfficiencyPending: Maybe<CmsToolTips>;
  milesPerDollar: Maybe<CmsToolTips>;
  skidControl: Maybe<CmsToolTips>;
  stoppingDistance: Maybe<CmsToolTips>;
  yearsAndMonths: Maybe<CmsToolTips>;
}

interface PriorityDetails {
  gaugeColor1?: string;
  gaugeColor2?: string;
  gaugeDescription1?: ReactNode;
  gaugeDescription2?: ReactNode;
  gaugeValue1?: number;
  gaugeValue2?: number;
  id: TreadWellPriorityEnum | string;
  noGaugeDescription1?: ReactNode;
  noGaugeDescription2?: ReactNode;
  pendingMsg?: ReactNode;
  title: string;
}

interface ProductFirstPriorityDetailsProps {
  dataReportUrl?: string;
  hideLink?: boolean;
  medianMileage?: number;
  minMileage?: number;
  onClick?: () => void;
  product: ProductData;
}

function ProductFirstPriorityDetails(props: ProductFirstPriorityDetailsProps) {
  const {
    dataReportUrl = 'https://www.discounttire.com/404',
    hideLink = false,
    medianMileage,
    minMileage,
    onClick,
    product
  } = props;

  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();
  const { aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.treadwellPdp
  );

  const contentMap: AemContentTypes = useMemo(() => {
    const priorityKeys = {
      evRange: null,
      evRangePending: null,
      fuelEfficiency: null,
      fuelEfficiencyPending: null,
      milesPerDollar: null,
      skidControl: null,
      stoppingDistance: null,
      yearsAndMonths: null
    };

    if (Object.keys(aemTooltipsContent).length > 0) {
      Object.keys(priorityKeys).forEach(key => {
        const tooltipContent = aemTooltipsContent?.[snakeCase(key)];

        if (tooltipContent) {
          priorityKeys[key] = tooltipContent;
        }
      });
    }

    return priorityKeys;
  }, [aemTooltipsContent]);

  const { selectedFitment } = useFitment();
  const isEVActive =
    selectedFitment?.fuelType === FuelTypeEnum.Bev && contentMap.evRange;
  const isEVPending =
    selectedFitment?.fuelType === FuelTypeEnum.Bev && contentMap.evRangePending;

  const {
    dryDistance,
    dryRating,
    rollingResistanceRating,
    wetDistance,
    wetRating,
    wetWornDistance
  } = product?.recommendationData?.stopping || product?.tdsData?.stopping || {};
  const { comfort, handling, quiet, wetHandling } =
    product?.recommendationData?.rideRating ||
    product?.tdsData?.rideRating ||
    {};
  const { tireLifeMonths, tireLifeYears } = calculateLifeExpectancy(
    product,
    15 * 1000,
    15 * 1000
  );
  const { milesPerDollar } = product?.recommendationData || {};
  const { mileage } = product?.recommendationData || product?.tdsData || {};
  const { median = 0 } = mileage || {};
  const actualPrice = product?.mapPrice?.value ?? product?.price?.value;
  const actualMilesPerDollar =
    milesPerDollar ?? Math.round(median / (actualPrice || 1));

  const { snowBrakeFeet, snowHandlingRating } =
    product?.recommendationData?.winter || {};

  const winterDetailsObj = aemTooltipsContent && {
    metric1Title: contentMap.skidControl?.title,
    metric1Tooltip: contentMap.skidControl?.tipText,
    metric1Value: snowHandlingRating,
    metric2Title: contentMap.stoppingDistance?.title,
    metric2Tooltip: contentMap.stoppingDistance?.tipText,
    metric2Value: snowBrakeFeet
  };

  const priorityDetailsArr: PriorityDetails[] = useMemo(
    () => [
      {
        gaugeColor1: '#3483DE',
        gaugeDescription1: (
          <>
            {(Boolean(wetDistance) || Boolean(wetWornDistance)) && (
              <Typography
                className="product-first-priority-details__description"
                size="small"
                styleName="no-wrap"
                tag="strong"
              >
                <div styleName="wet-roads">Wet Roads</div>
                <span styleName="new-tread">
                  New tread: <b>{Math.floor(wetDistance || 0)} ft.</b>
                </span>
                <span>
                  Worn tread: <b>{Math.floor(wetWornDistance || 0)} ft.</b>
                </span>
              </Typography>
            )}
          </>
        ),
        gaugeDescription2: (
          <>
            {Boolean(dryDistance) && (
              <>
                <Typography
                  className="product-first-priority-details__description"
                  size="small"
                  tag="strong"
                >
                  Dry Roads
                </Typography>
                <Typography
                  className="product-first-priority-details__description"
                  size="small"
                  tag="span"
                  weight="black"
                >
                  {Math.floor(dryDistance || 0)} ft.
                </Typography>
              </>
            )}
          </>
        ),
        gaugeValue1: wetRating || 0,
        gaugeValue2: dryRating || 0,
        id: TreadWellPriorityEnum.StoppingDistance,
        title: 'Stopping Distance at 60 mph'
      },
      {
        gaugeDescription1: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            Quiet Ride
          </Typography>
        ),
        gaugeDescription2: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            Comfort
          </Typography>
        ),
        gaugeValue1: quiet || 0,
        gaugeValue2: comfort || 0,
        id: TreadWellPriorityEnum.ComfortAndNoise,
        title: 'Ride Quality'
      },
      ...(isEVActive || contentMap.fuelEfficiency
        ? [
            {
              gaugeColor1: '#52A240',
              gaugeDescription1: (
                <Typography
                  className="product-first-priority-details__description"
                  size="small"
                  styleName="no-wrap"
                  tag="strong"
                >
                  {isEVActive
                    ? contentMap.evRange?.title
                    : contentMap.fuelEfficiency?.title}{' '}
                  <Tooltip
                    content={
                      isEVActive
                        ? contentMap.evRange?.tipText
                        : contentMap.fuelEfficiency?.tipText
                    }
                  />
                </Typography>
              ),
              gaugeValue1: rollingResistanceRating || 0,
              id: TreadWellPriorityEnum.FuelEfficiency,
              title: 'Efficiency'
            }
          ]
        : []),
      {
        gaugeDescription1: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            Steering Response
          </Typography>
        ),
        gaugeDescription2: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            Wet Handling Response
          </Typography>
        ),
        gaugeValue1: handling || 0,
        gaugeValue2: wetHandling || 0,
        id: TreadWellPriorityEnum.Handling,
        title: 'Handling'
      },
      {
        id: TreadWellPriorityEnum.LifeOfTire,
        noGaugeDescription1: contentMap.yearsAndMonths ? (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            <div styleName="counter-time-wrapper">
              <div styleName="counter-value">{tireLifeYears}</div>
              <div styleName="counter-label--years">years</div>
              <div styleName="counter-value">{tireLifeMonths}</div>
              <div styleName="counter-label">months</div>
              <Tooltip
                content={contentMap.yearsAndMonths?.tipText}
                styleName="tooltip"
              />
            </div>
          </Typography>
        ) : undefined,
        noGaugeDescription2:
          contentMap.milesPerDollar && actualMilesPerDollar ? (
            <Typography
              className="product-first-priority-details__description"
              size="small"
              tag="strong"
            >
              <div styleName="counter-miles-wrapper">
                <div data-testid="counter-value" styleName="counter-value">
                  {actualMilesPerDollar}
                </div>
                <div styleName="counter-label">
                  <div>miles per</div>
                  <div>dollar</div>
                </div>
                <Tooltip
                  content={contentMap.milesPerDollar.tipText}
                  styleName="tooltip"
                />
              </div>
            </Typography>
          ) : undefined,
        title: 'Tire Life & Cost'
      },
      ...(isEVPending || contentMap.fuelEfficiencyPending
        ? [
            {
              id: 'noRollingResistance',
              pendingMsg: (
                <Typography
                  className="product-first-priority-details__description"
                  size="small"
                >
                  <span styleName="pending-msg">
                    {isEVPending
                      ? contentMap.evRangePending?.title
                      : contentMap.fuelEfficiencyPending?.title}
                  </span>
                  <Tooltip
                    content={
                      isEVPending
                        ? contentMap.evRangePending?.tipText
                        : contentMap.fuelEfficiencyPending?.tipText
                    }
                    styleName="tooltip"
                  />
                </Typography>
              ),
              title: 'Efficiency'
            }
          ]
        : [])
    ],
    [
      comfort,
      contentMap,
      dryDistance,
      dryRating,
      handling,
      isEVActive,
      isEVPending,
      actualMilesPerDollar,
      quiet,
      rollingResistanceRating,
      tireLifeMonths,
      tireLifeYears,
      wetDistance,
      wetHandling,
      wetRating,
      wetWornDistance
    ]
  );
  const [detailsObj, setDetailsObj] = useState<PriorityDetails>(
    priorityDetailsArr[0]
  );

  useEffect(() => {
    function getNumber1PriorityId() {
      let priorityId: TreadWellPriorityEnum | string =
        TreadWellPriorityEnum.StoppingDistance;

      if (treadwellPreferences) {
        if (treadwellPreferences.isCustomDriverTypeShowing) {
          const customDriverType =
            treadwellPreferences.selectedCustomDrivingPriorities as CmsPromotionBannerComponent[];
          priorityId = customDriverType[0].id as TreadWellPriorityEnum;
        } else if (treadwellPreferences.selectedDriverType) {
          const driverType =
            treadwellPreferences.selectedDriverType as CmsToolTipsComponent;
          const tooltips = driverType?.tooltips as CmsToolTips[];
          priorityId = tooltips?.[0]?.id as TreadWellPriorityEnum;
        }
      }

      if (
        priorityId === TreadWellPriorityEnum.FuelEfficiency &&
        rollingResistanceRating === 0
      ) {
        priorityId = 'noRollingResistance';
      }

      return priorityId;
    }

    const detailsObj = priorityDetailsArr.filter(
      item => item.id === getNumber1PriorityId()
    )[0];

    setDetailsObj(detailsObj);
  }, [
    isEVActive,
    priorityDetailsArr,
    rollingResistanceRating,
    treadwellPreferences
  ]);

  return (
    <div className="product-first-priority-details" styleName="container">
      {treadwellPreferences?.showWinterTires ? (
        <>
          {winterDetailsObj &&
            winterDetailsObj.metric1Value &&
            winterDetailsObj.metric2Value && (
              <>
                <Typography styleName="winter-metric" weight="black">
                  {winterDetailsObj.metric1Title}
                  <Tooltip
                    content={winterDetailsObj.metric1Tooltip}
                    styleName="winter-tooltip"
                  />{' '}
                  <span styleName="winter-value">
                    {winterDetailsObj.metric1Value}{' '}
                  </span>
                  <span styleName="winter-slash">/ 5</span>
                </Typography>
                <Typography weight="black">
                  {winterDetailsObj.metric2Title}
                  <Tooltip
                    content={winterDetailsObj.metric2Tooltip}
                    styleName="winter-tooltip"
                  />{' '}
                  <span styleName="winter-value">
                    {winterDetailsObj.metric2Value}
                  </span>{' '}
                  <span styleName="winter-feet">FEET</span>
                </Typography>
              </>
            )}
        </>
      ) : (
        <>
          <div styleName="header">
            <Typography
              className="product-first-priority-details__header"
              styleName="title"
              tag="b"
              weight="black"
            >
              {detailsObj?.title}
            </Typography>
          </div>
          <div styleName="footer">
            {Boolean(detailsObj?.gaugeValue1) && (
              <div styleName="gauge-container">
                {isTreadwellFlow && (
                  <Gauge styleName="gauge" value={detailsObj.gaugeValue1} />
                )}
                <span>{detailsObj?.gaugeDescription1}</span>
              </div>
            )}
            {Boolean(detailsObj?.gaugeValue2) && (
              <div styleName="gauge-container">
                {isTreadwellFlow && (
                  <Gauge styleName="gauge" value={detailsObj.gaugeValue2} />
                )}
                <span>{detailsObj?.gaugeDescription2}</span>
              </div>
            )}
            {detailsObj?.noGaugeDescription1 && (
              <div styleName="gauge-container">
                <span>{detailsObj.noGaugeDescription1}</span>
              </div>
            )}
            {detailsObj?.noGaugeDescription2 && (
              <div styleName="gauge-container">
                <span>{detailsObj.noGaugeDescription2}</span>
              </div>
            )}
            {detailsObj?.pendingMsg && (
              <div styleName="pending-msg-container">
                {detailsObj.pendingMsg}
              </div>
            )}
          </div>
        </>
      )}

      {medianMileage && isTreadwellFlow && (
        <TreadLifeEstimate
          medianMileage={medianMileage}
          minMileage={minMileage}
          styleName="tread-life"
        />
      )}

      {!hideLink && (
        <Button
          href={`${dataReportUrl}#treadwell-data`}
          onClick={onClick}
          size="regular"
          styleName="button"
        >
          see full data report
        </Button>
      )}
    </div>
  );
}

export default ProductFirstPriorityDetails;
